import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@workspace/ui/components/chart'
import { Area, Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts'

export type ChartSeries = {
  key: string
  label: string
  /** CSS color, e.g. 'var(--chart-1)'. */
  color: string
  kind?: 'bar' | 'line' | 'area'
}

function formatDay(date: string): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Daily time-series chart. Bars are stacked when `stacked` is set; line and
 * area series render on top of bars when mixed.
 *
 * Area series carry a soft gradient fill — use them when the shape of a trend
 * matters more than reading an exact value off the axis.
 */
export function TimeSeriesChart({
  data,
  series,
  stacked = false,
  valueFormatter,
  className,
}: {
  data: Array<Record<string, number | string>>
  series: ChartSeries[]
  stacked?: boolean
  valueFormatter?: (value: number) => string
  className?: string
}) {
  const config = Object.fromEntries(
    series.map((s) => [s.key, { label: s.label, color: s.color }])
  ) satisfies ChartConfig

  return (
    <ChartContainer config={config} className={className ?? 'aspect-auto h-56 w-full'}>
      <ComposedChart data={data} margin={{ left: -16, right: 8, top: 4 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={formatDay}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={56}
          allowDecimals={false}
          tickFormatter={valueFormatter}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) => formatDay(String(payload?.[0]?.payload?.date ?? ''))}
              formatter={
                valueFormatter
                  ? (value, name, item) => (
                      <>
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="flex-1 text-muted-foreground">
                          {config[String(name)]?.label ?? name}
                        </span>
                        <span className="font-mono font-medium tabular-nums">
                          {valueFormatter(Number(value))}
                        </span>
                      </>
                    )
                  : undefined
              }
            />
          }
        />
        {series.length > 1 && <ChartLegend content={<ChartLegendContent />} />}
        <defs>
          {series
            .filter((s) => s.kind === 'area')
            .map((s) => (
              <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={`var(--color-${s.key})`} stopOpacity={0.3} />
                <stop offset="95%" stopColor={`var(--color-${s.key})`} stopOpacity={0.02} />
              </linearGradient>
            ))}
        </defs>
        {series.map((s) =>
          s.kind === 'area' ? (
            <Area
              key={s.key}
              dataKey={s.key}
              stroke={`var(--color-${s.key})`}
              strokeWidth={2}
              fill={`url(#fill-${s.key})`}
              dot={false}
            />
          ) : s.kind === 'line' ? (
            <Line
              key={s.key}
              dataKey={s.key}
              stroke={`var(--color-${s.key})`}
              strokeWidth={2}
              dot={false}
            />
          ) : (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId={stacked ? 'stack' : undefined}
              fill={`var(--color-${s.key})`}
              radius={2}
            />
          )
        )}
      </ComposedChart>
    </ChartContainer>
  )
}
